import React, { useEffect, useState, useMemo } from "react";
import { Rnd } from "react-rnd";
import { createPortal } from "react-dom";
import {
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { CopyAll, Close, UnfoldLess, UnfoldMore, Edit } from "@mui/icons-material";
import { getDropDownVal } from "../dropdownUtils";

const SuggestionActReferenceModal = ({ open, onClose, data }) => {
  const [type, setType] = useState("1");
  const [search, setSearch] = useState("");
  const [minimized, setMinimized] = useState(true);
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [actrefOptions, setActrefOptions] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editText, setEditText] = useState("");

  const [minimizedSize, setMinimizedSize] = useState({
    width: Math.max(250, window.innerWidth * 0.2),
    height: Math.max(100, window.innerHeight * 0.15),
  });

  const [maximizedSize, setMaximizedSize] = useState({
    width: Math.min(600, window.innerWidth * 0.5),
    height: Math.min(500, window.innerHeight * 0.75),
  });

  const [rndSize, setRndSize] = useState(maximizedSize);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [localData, setLocalData] = useState([]);

  // Adjust sizes on window resize
  useEffect(() => {
    const updateSizes = () => {
      setMinimizedSize({
        width: Math.max(250, window.innerWidth * 0.2),
        height: Math.max(100, window.innerHeight * 0.15),
      });
      setMaximizedSize({
        width: Math.min(600, window.innerWidth * 0.5),
        height: Math.min(500, window.innerHeight * 0.75),
      });
    };

    window.addEventListener("resize", updateSizes);
    return () => window.removeEventListener("resize", updateSizes);
  }, []);

  useEffect(() => {
    if (open) {
      setMinimized(false); // always expanded

      const centerX = (window.innerWidth - maximizedSize.width) / 2;
      const centerY = (window.innerHeight - maximizedSize.height) / 2;

      setRndSize(maximizedSize);
      setPosition({
        x: centerX > 0 ? centerX : 0,
        y: centerY > 0 ? centerY : 0
      });
    }
  }, [open, maximizedSize]);



  useEffect(() => {
    setRndSize(minimized ? minimizedSize : maximizedSize);
  }, [minimized, minimizedSize, maximizedSize]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    try {
      const sugg = getDropDownVal("SUGG") || [];
      setSuggestionOptions(sugg.map(op => ({ value: op.code, label: op.value })));

      const acts = getDropDownVal("ACTF") || [];
      setActrefOptions(acts.map(op => ({ value: op.code, label: op.value })));
    } catch (err) {
      console.warn("Error loading dropdowns");
    }
  }, []);

  const handleCopy = (text) => navigator.clipboard.writeText(text);
  const handleClose = () => onClose();
  const toggleMinimize = () => setMinimized(prev => !prev);

  const getHeaderLabel = (headerCode) => {
    if (type === "3") {
      return 'Observation';
    } else {
      const options = type === "1" ? suggestionOptions : actrefOptions;
      const found = options.find(opt => opt.value === headerCode);
      return found ? found.label : String(headerCode || '');
    }

  };

  const filtered = useMemo(() => {
    const optionsReady =
      (type === "1" && suggestionOptions.length > 0) ||
      (type === "2" && actrefOptions.length > 0) ||
      (type === "3") ||
      (type === "4");

    if (!optionsReady) return [];

    return localData
      .filter(item => item.type.toString() === type)
      .map(item => ({ ...item, headerLabel: getHeaderLabel(item.header) }))
      .filter(item =>
      (item.headerLabel?.toLowerCase().includes(search.toLowerCase()) ||
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.content?.toLowerCase().includes(search.toLowerCase()))
      );
  }, [localData, type, suggestionOptions, actrefOptions, search]);

  const handleEditClick = (item) => {
    setEditItem(item);
    setEditText(item.content);
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    if (editItem) {
      setLocalData(prev =>
        prev.map(d => (d.id === editItem.id ? { ...d, content: editText } : d))
      );
    }
    setEditModalOpen(false);
  };

  if (!open) return null;


  return createPortal(
    <>
      <Rnd
        position={position}
        size={rndSize}
        bounds="window"
        onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
        onResizeStop={(e, dir, ref, delta, newPos) => {
          setRndSize({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
          });
          setPosition(newPos);
        }}
        minWidth={300}
        minHeight={200}
        enableResizing={!minimized}
        disableDragging={editModalOpen}
        dragHandleClassName="drag-header"
        style={{
          zIndex: 1300,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          background: "white",
        }}
      >

        <Paper
          elevation={4}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: 1,
            overflow: "auto",
          }}
        >
          <Box
            className="drag-header"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "move",
              px: 1,
              pb: 1,
              userSelect: "none",
            }}
          >
            <Typography fontWeight="bold">Suggestions / Act References</Typography>
            <Box>
              <Tooltip title={minimized ? "Expand" : "Minimize"}>
                <IconButton size="small" onClick={toggleMinimize}>
                  {minimized ? <UnfoldMore fontSize="small" /> : <UnfoldLess fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={handleClose}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(e, val) => val && setType(val)}
            size="small"
            sx={{ my: 1 }}
          >
            <ToggleButton value="1">Suggestion</ToggleButton>
            <ToggleButton value="2">Act Reference</ToggleButton>
            <ToggleButton value="3">Observations</ToggleButton>
            <ToggleButton value="4">Acknowledgement</ToggleButton>
          </ToggleButtonGroup>

          {!minimized && (
            <>
              <TextField
                fullWidth
                variant="outlined"
                label="Search by Header"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2, "& .MuiInputBase-root": { height: 38 } }}
              />

              <TableContainer sx={{ maxHeight: rndSize.height - 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Header</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Content</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length > 0 ? (
                      filtered.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.headerLabel}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell sx={{ whiteSpace: "pre-wrap" }}>{item.content}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Copy">
                              <IconButton size="small" onClick={() => handleCopy(item.content)}>
                                <CopyAll fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditClick(item)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          {(type === "1" && suggestionOptions.length === 0) ||
                            (type === "2" && actrefOptions.length === 0)
                            ? "Loading options..."
                            : "No records found."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      </Rnd>

      {editModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 50,
            left: 290,
            width: "80%",
            maxWidth: 500,
            maxHeight: "80vh",
            backgroundColor: "white",
            border: "2px solid #1976d2",
            borderRadius: 2,
            boxShadow: 5,
            zIndex: 2000,
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Edit Content
          </Typography>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 1,
            }}
          >
            <TextField
              multiline
              fullWidth
              minRows={6}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              variant="outlined"
              autoFocus
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: 1,
            }}
          >
            <Button onClick={() => setEditModalOpen(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} variant="contained">
              Save
            </Button>
          </Box>
        </Box>
      )}
    </>,
    document.body
  );
};

export default SuggestionActReferenceModal;
